import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { activityActionLabel } from '../lib/types';
import type { ProjectActivity } from '../lib/types';

/**
 * What has been happening on this project lately.
 *
 * The audit trail has been written to since the first migration and never read back — every
 * change in the Hub recorded, and no way to look at any of it.
 *
 * WHAT IS SHOWN IS DECIDED BY THE API, not here. A customer gets an allowlist of their own
 * work moving; our staff see the plan's mechanics and the service loop as well; only an
 * administrator sees the raw details. The card renders whatever comes back and adds nothing —
 * a second opinion in the browser about what a customer may see is a second place to get it
 * wrong.
 */
export function ActivityCard({ projectId }: { projectId: string }) {
  const [data, setData] = useState<ProjectActivity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [find, setFind] = useState('');
  /** How many to fetch. Ten is a glance; the rest are there for somebody who is looking. */
  const [limit, setLimit] = useState(10);

  const load = useCallback(async () => {
    const query = new URLSearchParams();
    if (from) query.set('from', from);
    if (to) query.set('to', to);
    query.set('limit', String(limit));
    try {
      setData(await api.get<ProjectActivity>(`/projects/${projectId}/activity?${query}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the activity');
    }
  }, [projectId, from, to, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Filtered in the browser, unlike the dates.
   *
   * The date range narrows what is fetched because the trail is long; the text box narrows
   * what is already on screen, which is the difference between "find that thing from March"
   * and "find that thing in this list".
   */
  const query = find.trim().toLowerCase();
  const shown = (data?.events ?? []).filter(
    (event) =>
      !query ||
      event.entityLabel.toLowerCase().includes(query) ||
      event.who.toLowerCase().includes(query) ||
      activityActionLabel(event.action).toLowerCase().includes(query),
  );

  return (
    <div className="card">
      {/*
        Everything that narrows the list sits in the heading, on one line: the dates, how many
        to fetch, and the text box. They were a separate block above the list, which pushed the
        entries themselves below the fold on a card that is meant to be glanced at.
      */}
      <div className="card-head wrap" style={{ rowGap: 8 }}>
        <h3>Lately</h3>
        <span className="row wrap" style={{ gap: 8 }}>
          {/*
            Only while searching. Unfiltered it printed the same number as the picker beside
            it — a bare "10" next to a select reading "10", which looks like one of them is
            broken rather than like a count.
          */}
          {data && query && (
            <span className="dim" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {shown.length} of {data.events.length}
            </span>
          )}

          <input
            type="date"
            className="input-sm"
            value={from}
            max={to || undefined}
            title="Show activity from this date"
            onChange={(event) => setFrom(event.target.value)}
          />
          <input
            type="date"
            className="input-sm"
            value={to}
            min={from || undefined}
            title="Show activity up to this date"
            onChange={(event) => setTo(event.target.value)}
          />
          {(from || to) && (
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              title="Clear the dates"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
            >
              Clear
            </button>
          )}

          <select
            className="input-sm"
            value={limit}
            title="How many to show"
            onChange={(event) => setLimit(Number(event.target.value))}
          >
            {[10, 25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>

          <input
            type="search"
            className="input-sm"
            style={{ minWidth: 150 }}
            value={find}
            placeholder="Find in this list"
            onChange={(event) => setFind(event.target.value)}
          />
        </span>
      </div>

      <div className="card-pad">
        {error && <div className="callout mb-2">{error}</div>}

        {!data ? (
          <span className="dim">Loading…</span>
        ) : shown.length === 0 ? (
          <p className="dim" style={{ margin: 0, fontSize: 13 }}>
            {data.events.length === 0
              ? from || to
                ? 'Nothing happened on this project in that period.'
                : 'Nothing recorded on this project yet.'
              : `Nothing in these ${data.events.length} entries matches “${find.trim()}”.`}
          </p>
        ) : (
          <div className="stack-sm">
            {shown.map((event) => (
              <div className="row" key={event.id} style={{ gap: 8, alignItems: 'baseline' }}>
                <span className="mono dim" style={{ fontSize: 11, minWidth: 116 }}>
                  {when(event.at)}
                </span>
                <span className="grow" style={{ minWidth: 0 }}>
                  <span className="row" style={{ gap: 7 }}>
                    <strong style={{ fontSize: 13 }}>{event.who}</strong>
                    <span className="badge">{activityActionLabel(event.action)}</span>
                  </span>
                  {event.entityLabel && <span className="doc-meta">{event.entityLabel}</span>}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* At the limit there may well be more — the count picker above is how you get them. */}
        {data && data.events.length >= limit && (
          <p className="dim mt-2" style={{ fontSize: 12, margin: '10px 0 0 0' }}>
            Showing the most recent {limit}. Change the number above to see further back.
          </p>
        )}
      </div>
    </div>
  );
}

/** Date and time to the minute — an audit line without a time is half an answer. */
function when(at: string): string {
  const date = new Date(at);
  return `${date.toLocaleDateString('en-ZA')} ${date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`;
}
